import type { Props as JoyrideProps, Step } from 'react-joyride'
import ReactJoyride from 'react-joyride'

export type CustomJoyrideStep = Step & {
  // Stable identifier (does not depend on order)
  id: string
  // optional information needed in the callback
  meta?: {
    // perform an automatic click
    clickSelector?: string
    // wait for an item to appear
    waitForSelector?: string
    // override next step
    nextStepId?: number
  }
}

/**
 * `meta` is a set of actions executed in `step:after` (i.e. AFTER the current step has been displayed).
 * It is typically used to prepare the UI for the next step: for example, in order to show the ADD_ITEM popup
 * in the following step (which targets the autocomplete input and prompts the user to add an article),
 * in the `step:after` of the current step (GO_TO_ADD_BY_CARD) I need to click on `clickSelector`
 * (`go-to-add-by-card-btn`) and only move forward once `waitForSelector` becomes available.
 *
 * In Joyride’s callback I also handle `step:before`: this is the point where I can prepare the UI BEFORE
 * Joyride renders the current step (so before it calculates the target element and the tooltip position).
 * The actions can also be simple (click + wait), but the difference compared to `meta` is temporal:
 * `step:before` prepares the current step before the popup becomes visible, while `meta` (in `step:after`)
 * displays the popup of the current step and prepares the next one.
 *
 * `meta` works well when:
 * - the current step targets an element that is already present (e.g. a button) and I want to automate the action
 *   the user would normally perform, in order to make the UI for the next step appear;
 * - or when I only need to "clean up" the UI (close menus, etc.) before the next step.
 *
 * Why can’t I always use `meta`?
 * - If the target of the step I’m about to show does not exist yet (menu/panel closed), I need to make it available
 *   BEFORE rendering the step → therefore I need `step:before` on the relevant step.
 * - Also, with conditional steps/jumps, the "previous step" might not be executed, so a `meta` placed there
 *   might never run.
 */

type Props = JoyrideProps & {
  steps: CustomJoyrideStep[]
}

export function CustomJoyrideTour({ steps, ...props }: Props) {
  return (
    <ReactJoyride
      steps={steps}
      {...props}
      styles={{
        options: {
          zIndex: 20000,
        },
        buttonNext: {
          backgroundColor: 'var(--warning-color)',
        },
        buttonBack: {
          color: 'var(--warning-color)',
          border: ' 1.5px solid var(--warning-color)',
          borderRadius: '4px',
        },
        buttonSkip: {
          color: 'var(--warning-color)',
          border: ' 1.5px solid var(--warning-color)',
          borderRadius: '4px',
        },
      }}
    />
  )
}
